import React from 'react'
import { DollarSign, Maximize2, Info, PieChart, Eye } from 'lucide-react'
import KPICard from '../../ui/KPICard'
import { describeViewers } from './broadcastCustomerFormat'
import { PublishedBroadcast } from '../../../services/broadcastService'
import { currencyOf,
  ExposureTotals, SPOT_SECONDS, formatAreaSeconds, formatMoney
} from '../broadcastValuation'

interface Props {
  broadcast: PublishedBroadcast
  totals: ExposureTotals
  rate: number | null
  /** Sponsored area-seconds over everything counted. Null when nothing counts. */
  sponsorShare: number | null
  /** Frames with a sponsor on screen, over frames analysed. Null when unmeasured. */
  presence: number | null
}

/**
 * What the broadcast was worth to its SPONSORS, above everything else.
 *
 * Four cards, and the two on the right are shares rather than totals. Time
 * on screen and a brand count used to sit here and were dropped: with the
 * page narrowed to sponsors, a brand count is the length of the table
 * directly below it, and raw seconds invited exactly the comparison
 * area-seconds exists to prevent — a board on screen for ten minutes at one
 * percent of frame is not worth ten minutes.
 *
 * Value leads because it is the number the reader came for, and area-seconds
 * sits beside it because value is derived from it — a reader who distrusts the
 * money can see the measurement it was built from without scrolling.
 *
 * WHEN THERE IS NO RATE the value card is replaced rather than zeroed. A report
 * that prints $0 against a real sponsor is worse than one that admits it was
 * never told the audience, and the missing input is a thing somebody can go and
 * fix.
 */
const ExposureHeadline: React.FC<Props> = ({
  broadcast, totals, rate, sponsorShare, presence
}) => {
  const currency = currencyOf(broadcast)

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {rate === null ? (
          <div className="col-span-2 lg:col-span-1 rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-4 flex flex-col justify-center">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
              Media value
            </p>
            <p className="text-sm font-medium text-amber-900 mt-1">Not priced</p>
            {/* Say which input is missing: "no audience" and "no CPM" are
                fixed by different people. */}
            <p className="text-[11px] text-amber-700 mt-1 leading-snug">
              {broadcast.viewers != null
                ? `Audience recorded (${Number(broadcast.viewers).toLocaleString()} viewers) but no CPM, so exposure cannot be converted to value yet.`
                : (broadcast.effective_cpm ?? broadcast.cpm) != null
                  ? 'CPM recorded but no audience, so exposure cannot be converted to value yet.'
                  : 'No audience or CPM recorded for this broadcast, so exposure cannot be converted to value.'}
            </p>
          </div>
        ) : (
          <KPICard
            label="Media value"
            value={formatMoney(totals.value, currency)}
            icon={DollarSign}
            colorScheme="emerald"
            tooltip={
              `${formatAreaSeconds(totals.areaSeconds)} area-seconds = ` +
              `${totals.equivalents.toFixed(2)} full-screen :${SPOT_SECONDS}s at ` +
              `${formatMoney(rate, currency)} per :${SPOT_SECONDS}. ` +
              `Rate is this broadcast's own audience x CPM, a planning assumption ` +
              `rather than a confirmed rate card.`
            }
          />
        )}

        <KPICard
          label="Area-seconds"
          value={formatAreaSeconds(totals.areaSeconds)}
          icon={Maximize2}
          colorScheme="blue"
          tooltip={
            'Screen area x time. One area-second is a brand filling the entire ' +
            'frame for one second, so a large logo briefly can outscore a small ' +
            'one for a long time. This is what value is calculated from.'
          }
        />

        {/* The shares sit WITH the totals now rather than below them,
            because the page is sponsors only: there is no second population
            on screen for a reader to confuse them with, and four cards is
            one row.

            Presence before share, so the row reads as a narrowing: what it
            was worth, what was measured, how often a sponsor was up, and how
            much of the brand space they held when they were. */}
        <KPICard
          label="Sponsor on screen"
          value={presence === null ? '—' : `${(presence * 100).toFixed(0)}%`}
          icon={Eye}
          colorScheme="blue"
          tooltip={
            'Share of the frames analysed in which at least one sponsored brand ' +
            'was visible. Counted once per frame however many sponsors are in ' +
            'shot, so it never exceeds 100% — a different question from ' +
            'area-seconds, which asks how much screen they filled.'
          }
        />
        <KPICard
          label="Share of exposure"
          value={sponsorShare === null ? '—' : `${(sponsorShare * 100).toFixed(0)}%`}
          icon={PieChart}
          colorScheme="emerald"
          tooltip={
            'Sponsored area-seconds as a share of every counted brand on this ' +
            'broadcast. The rest is competitors and incidental brands — exposure ' +
            'that happened but nobody paid for.'
          }
        />
      </div>

      {rate !== null && (
        <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-px text-gray-400" />
          <span>
            Priced at <strong className="font-medium text-gray-700">
              {formatMoney(rate, currency)} per :{SPOT_SECONDS}
            </strong>
            {broadcast.viewers != null && (
              <> — {describeViewers(broadcast)}
                 {(broadcast.effective_cpm ?? broadcast.cpm) != null && (
                   <> at {formatMoney(Number(broadcast.effective_cpm ?? broadcast.cpm), currency)} CPM</>
                 )}</>
            )}
            . Planning assumption, not a confirmed rate.
          </span>
        </p>
      )}
    </div>
  )
}

export default ExposureHeadline
